/** @jsx jsx */
import { jsx, css } from '@emotion/core';

import React, {
  Fragment,
  useCallback,
  useState,
  useContext,
  useEffect,
} from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
  Grid,
  Box,
  Link,
  CircularProgress,
  FormHelperText,
} from '@material-ui/core';
import { ErrorCode, useDropzone } from 'react-dropzone';
import UploadIcon from './static/uploadIcon.svg';
import colors from '../../themes/main-colors';
import useFileUploadApi from '../../hooks/pge-plus/useFileUploadApi';
import { NotificationsContext } from '../../providers/NotificationsProvider';
import useContentMgmt from '../../hooks/useContentMgmt';
import microcopyGroupIds from './microcopyGroupIds';
import imageCompression from 'browser-image-compression';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    link: {
      fontSize: '16px !important',
      padding: '0px !important',
      color: `${colors.sparkBlue} !important`,
      fontWeight: 600,
    },
    image: {
      border: `2px solid ${colors.sparkBlue} !important`,
      borderRadius: '5px',
    },
  }),
);

type Props = {
  placeholder: React.ReactNode;
  reuploadLabel: string;
  image?: string | undefined; //S3 file object name for already uploaded image. eg., a72a7e65-25e3-4f7a-8f02-2b10febf92d3.jpg
  onUpload: (pgeStorekey: string, fileName?: string) => void;
  style?: React.CSSProperties | undefined;
  acceptMimeTypes?: Record<string, []>;
  maxFileSize?: number;
};

export const DEFAULT_MIME_TYPES: Record<string, []> = {
  'image/png': [],
  'image/jpeg': [],
};
const DEFAULT_MAX_FILE_SIZE_COMPRESSED = 1024 * 1024 * 4.5; //4.5MB

//Users will be able to pick image files upto 25 MB, but should be within 6MB after compression.
const DEFAULT_MAX_FILE_SIZE = 1024 * 1024 * 25; //25MB.

export const FILE_TYPE_PDF = 'application/pdf';

const MimeTypeMap: { [key: string]: string } = {
  'image/png': 'PNG',
  'image/jpeg': 'JPG',
  [FILE_TYPE_PDF]: 'PDF',
};

export const MIME_TYPE_PDF: Record<string, []> = {
  [FILE_TYPE_PDF]: [],
};

/**
 * Checks if the provided URL points to a PDF file.
 * @param url - The URL to check.
 * @returns True if the URL is a PDF file, false otherwise.
 */
export function isPdfFile(url: string): boolean {
  // Use a URL object to parse the URL and get the pathname
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;

    // Get the file extension by splitting the pathname by '.' and taking the last part
    const extension = pathname
      .split('.')
      .pop()
      ?.toLowerCase();

    // Check if the extension is 'pdf'
    return extension === 'pdf';
  } catch (error) {
    return false;
  }
}

export const getSupportedFileNames = (mimeTypes: string[]) => {
  const fileTypes = mimeTypes?.map(mimeType => MimeTypeMap[mimeType] || null);
  return fileTypes.filter(v => v).join(', ');
};

export const FileUploader = ({
  image,
  placeholder,
  reuploadLabel,
  onUpload,
  style,
  acceptMimeTypes = DEFAULT_MIME_TYPES,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
}: Props) => {
  const [filePreview, setFilePreview] = useState<string>();
  const [fileType, setFileType] = useState<string>();

  const { uploadFile, loading, getPgeStoreDownloadUrl } = useFileUploadApi();
  const notificationContext = useContext(NotificationsContext);
  const [fileUploadError, setFileUploadError] = useState<React.ReactNode>('');
  const [isCompressing, setIsCompressing] = useState(false);
  const { content, inlineRichText } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_COMMON_FILE_UPLOAD_COMPONENT,
  );

  const classes = useStyles();
  useEffect(() => {
    //Get the download URL for the image
    if (image) {
      void getImageUrl(image);
    }
  }, [image]);

  const getImageUrl = async (key: string) => {
    //Get the file download URL
    try {
      const images = await getPgeStoreDownloadUrl({ keys: [key] });
      if (isPdfFile(images[0].downloadUrl)) {
        setFileType(FILE_TYPE_PDF);
      }
      setFilePreview(images[0].downloadUrl);
    } catch (e) {
      notificationContext.setState({
        isOpen: true,
        severity: 'error',
        variant: 'filled',
        message: content.get('FILE_UPLOAD_FAILED_MESSAGE'),
      });
      window.scrollTo(0, 0);
    }
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      let file = acceptedFiles[0];

      //Due to C2M limitation of loading images greater than 6MB, we compress and upload the files.
      //After compression, the file cannot be more than 6MB
      if (
        file.type !== FILE_TYPE_PDF &&
        file.size > DEFAULT_MAX_FILE_SIZE_COMPRESSED
      ) {
        setFilePreview(URL.createObjectURL(file));
        setIsCompressing(true);
        try {
          const compressed = await imageCompression(file, {
            maxSizeMB: 1,
            useWebWorker: true,
            initialQuality: 0.6,
          });
          setIsCompressing(false);
          //Do Compression and check file size
          if (compressed?.size > DEFAULT_MAX_FILE_SIZE_COMPRESSED) {
            setFileUploadError(
              content.get('FILE_SIZE_EXCEEDS_THE_LIMIT') || '',
            );
            setIsCompressing(false);
            return;
          }
          //Replace the original file with compressed one
          file = compressed;
        } catch (error) {
          setFileUploadError('Image compression failed');
          setIsCompressing(false);
          return;
        }
      } else {
        if (file.size > DEFAULT_MAX_FILE_SIZE_COMPRESSED) {
          setFileUploadError(content.get('FILE_SIZE_EXCEEDS_THE_LIMIT') || '');
          return;
        }
        setFilePreview(URL.createObjectURL(file));
      }

      setFileType(file.type);
      setFileUploadError('');

      const response = await uploadFile(file);

      if (response.success && response.pgeStoreKey) {
        onUpload(response.pgeStoreKey, acceptedFiles[0].name);
      } else {
        notificationContext.setState({
          isOpen: true,
          severity: 'error',
          variant: 'filled',
          message: content.get('FILE_UPLOAD_FAILED_MESSAGE'),
        });
        window.scrollTo(0, 0);
        setFilePreview(undefined);
      }
    },
    [content],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDropAccepted: onDrop,
    onDropRejected: rejectedFiles => {
      const errorCode = rejectedFiles[0]?.errors[0]?.code;
      if (errorCode === ErrorCode.FileInvalidType) {
        setFileUploadError(
          inlineRichText(content.get('INCORRECT_FILE_TYPE') || '', {
            SUPPORTED_FILE_TYPES: getSupportedFileNames(
              Object.keys(acceptMimeTypes),
            ),
          }) || '',
        );
      }
      if (errorCode === ErrorCode.FileTooLarge) {
        setFileUploadError(
          content.get('MAX_ALLOWED_PGE_PLUS_FILE_LIMIT') || '',
        );
      }
    },
    accept: acceptMimeTypes,
    maxFiles: 1,
    multiple: false,
    maxSize: maxFileSize,
  });

  const uploadAreaStyle: React.CSSProperties = {
    padding: '24px 0',
    marginTop: 24,
    marginBottom: 8,
    display: 'flex',
    justifyContent: 'center',
    borderRadius: '5px',
    cursor: 'pointer',
    border: '2px dashed ' + colors.sparkBlue,
    backgroundColor: colors.lightBlue1,
  };

  return (
    <Fragment>
      <input {...getInputProps()} />
      {!filePreview && (
        <Grid
          {...getRootProps()}
          css={{
            ...uploadAreaStyle,
            borderColor: fileUploadError ? colors.errorRed : colors.sparkBlue,
            visibility: filePreview ? 'hidden' : 'visible',
          }}
          style={style}
        >
          {isDragActive ? (
            <Box
              style={{
                fontSize: 16,
                fontWeight: 'bold',
                lineHeight: '20px',
                color: colors.sparkBlue,
              }}
            >
              <UploadIcon
                fontSize="large"
                css={{ verticalAlign: 'middle', marginRight: 18 }}
              />{' '}
              {content.get('DROP_FILES_HERE')}
            </Box>
          ) : (
            <Box
              style={{
                fontSize: 16,
                fontWeight: 'bold',
                lineHeight: '20px',
                color: colors.sparkBlue,
              }}
            >
              {' '}
              <UploadIcon
                fontSize="large"
                css={{ verticalAlign: 'middle', marginRight: 18 }}
              />
              {placeholder}
            </Box>
          )}
        </Grid>
      )}

      {filePreview && (
        <Fragment>
          <Box css={{ position: 'relative' as const }}>
            {fileType !== FILE_TYPE_PDF && (
              <img
                className={classes.image}
                src={filePreview}
                css={{
                  width: '100%',
                  maxHeight: '400px',
                  objectFit: 'cover',
                }}
                // Revoke data uri after image is loaded
                onLoad={() => {
                  URL.revokeObjectURL(filePreview);
                }}
              />
            )}
            {fileType === FILE_TYPE_PDF && (
              <Box
                style={{
                  ...uploadAreaStyle,
                }}
              >
                <iframe
                  src={filePreview}
                  css={{ height: '300px', width: '90%' }}
                />
              </Box>
            )}
            {(loading || isCompressing) && (
              <Box
                flexDirection={'column'}
                justifyContent={'center'}
                display={'flex'}
                alignItems={'center'}
                position={'absolute'}
                css={{
                  width: '100%',
                  height: '100%',
                  top: 0,
                  background: '#dcdcdc9e',
                }}
              >
                <Box>
                  <CircularProgress />
                </Box>
              </Box>
            )}
          </Box>
          {!loading && (
            <Link
              underline="none"
              component="button"
              variant="body1"
              className={classes.link}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                open();
              }}
            >
              {reuploadLabel}
            </Link>
          )}
        </Fragment>
      )}
      {fileUploadError && (
        <FormHelperText css={{ fontFamily: 'inherit' }} error>
          {fileUploadError}
        </FormHelperText>
      )}
    </Fragment>
  );
};
